import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { apiFetch } from '../api';
import './Profile.css';

interface ProfileProps {
  userEmail: string;
  onProfileUpdate: (prenom: string) => void;
}

interface UserProfile {
  nom: string;
  prenom: string;
  email: string;
  societe: string;
  siret: string;
  tva: string;
  createdAt: string;
}

function Profile({ userEmail, onProfileUpdate }: ProfileProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    societe: '',
    siret: '',
    tva: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [siretLoading, setSiretLoading] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [userEmail]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`/api/profile?email=${encodeURIComponent(userEmail)}`);
      const data = await response.json();

      if (data.success) {
        setProfile(data.profile);
        setFormData({
          nom: data.profile.nom,
          prenom: data.profile.prenom,
          societe: data.profile.societe,
          siret: data.profile.siret,
          tva: data.profile.tva,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSiretChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const siret = e.target.value.replace(/\s/g, '');
    setFormData({ ...formData, siret });

    if (siret.length === 14) {
      setSiretLoading(true);
      setError('');

      try {
        const response = await fetch(`${API_URL}/api/auth/company-info/${siret}`);
        const data = await response.json();

        if (data.success) {
          setFormData({
            ...formData,
            siret,
            societe: data.data.nom,
            tva: data.data.tva
          });
        } else {
          setError('SIRET non trouvé dans la base de données');
        }
      } catch (err) {
        setError('Erreur lors de la vérification du SIRET');
      } finally {
        setSiretLoading(false);
      }
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.nom || !formData.prenom) {
      setError('Le nom et le prénom sont obligatoires');
      return;
    }

    // Si changement de mot de passe
    if (formData.newPassword) {
      if (!formData.currentPassword) {
        setError('Veuillez entrer votre mot de passe actuel');
        return;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return;
      }
      if (formData.newPassword.length < 6) {
        setError('Le nouveau mot de passe doit contenir au moins 6 caractères');
        return;
      }
    }

    try {
      const response = await apiFetch(`/api/profile`, {
        method: 'PUT',
        body: JSON.stringify({
          email: userEmail,
          ...formData
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Profil mis à jour avec succès');
        setEditing(false);
        loadProfile();
        onProfileUpdate(formData.prenom);
        // Réinitialiser les champs de mot de passe
        setFormData({
          ...formData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Erreur lors de la mise à jour du profil');
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setError('Veuillez entrer votre mot de passe');
      return;
    }

    try {
      const response = await apiFetch(`/api/profile`, {
        method: 'DELETE',
        body: JSON.stringify({
          email: userEmail,
          password: deletePassword
        })
      });

      const data = await response.json();

      if (data.success) {
        // Déconnexion et redirection
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userPrenom');
        window.location.href = '/';
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Erreur lors de la suppression du compte');
    }
  };

  if (loading) {
    return (
      <div className="profile">
        <div className="profile-loading">Chargement...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile">
        <div className="profile-error">Profil non trouvé</div>
      </div>
    );
  }

  return (
    <div className="profile">
      <div className="profile-header">
        <h1>Mon Profil</h1>
        {!editing && (
          <button className="btn-edit" onClick={() => setEditing(true)}>
            Modifier
          </button>
        )}
      </div>

      {error && <div className="profile-error">{error}</div>}
      {success && <div className="profile-success">{success}</div>}

      {!editing ? (
        <div className="profile-view">
          <div className="profile-section">
            <h2>Informations personnelles</h2>
            <div className="profile-grid">
              <div className="profile-item">
                <label>Prénom</label>
                <div className="profile-value">{profile.prenom}</div>
              </div>
              <div className="profile-item">
                <label>Nom</label>
                <div className="profile-value">{profile.nom}</div>
              </div>
              <div className="profile-item">
                <label>Email</label>
                <div className="profile-value">{profile.email}</div>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2>Informations entreprise</h2>
            <div className="profile-grid">
              <div className="profile-item">
                <label>Société</label>
                <div className="profile-value">{profile.societe || '-'}</div>
              </div>
              <div className="profile-item">
                <label>SIRET</label>
                <div className="profile-value">{profile.siret || '-'}</div>
              </div>
              <div className="profile-item">
                <label>N° TVA</label>
                <div className="profile-value">{profile.tva || '-'}</div>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2>Informations du compte</h2>
            <div className="profile-item">
              <label>Date de création</label>
              <div className="profile-value">
                {new Date(profile.createdAt).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </div>
            </div>
          </div>

          <div className="profile-danger-zone">
            <h2>Zone de danger</h2>
            <p>La suppression de votre compte est irréversible. Toutes vos données seront définitivement supprimées.</p>
            <button className="btn-delete" onClick={() => setShowDeleteModal(true)}>
              Supprimer mon compte
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleUpdateProfile} className="profile-form">
          <div className="profile-section">
            <h2>Informations personnelles</h2>
            <div className="profile-grid">
              <div className="form-group">
                <label>Prénom *</label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2>Informations entreprise</h2>
            <p className="section-hint">Modifiez le SIRET pour mettre à jour automatiquement le nom de l'entreprise et le n° TVA</p>
            <div className="profile-grid">
              <div className="form-group">
                <label>SIRET *</label>
                <input
                  type="text"
                  name="siret"
                  value={formData.siret}
                  onChange={handleSiretChange}
                  placeholder="14 chiffres"
                  maxLength={14}
                />
                {siretLoading && <span style={{ fontSize: '0.85rem', color: '#666' }}>Vérification du SIRET...</span>}
              </div>
              <div className="form-group">
                <label>Société</label>
                <input
                  type="text"
                  name="societe"
                  value={formData.societe}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed', color: '#666' }}
                />
              </div>
              <div className="form-group">
                <label>N° TVA</label>
                <input
                  type="text"
                  name="tva"
                  value={formData.tva}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed', color: '#666' }}
                />
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2>Changer le mot de passe</h2>
            <p className="section-hint">Laissez vide si vous ne souhaitez pas changer de mot de passe</p>
            <div className="profile-grid">
              <div className="form-group">
                <label>Mot de passe actuel</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  autoComplete="current-password"
                />
              </div>
              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label>Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>

          <div className="profile-actions">
            <button type="button" className="btn-cancel" onClick={() => {
              setEditing(false);
              setError('');
              loadProfile();
            }}>
              Annuler
            </button>
            <button type="submit" className="btn-save">
              Enregistrer les modifications
            </button>
          </div>
        </form>
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Supprimer mon compte</h2>
            <p>Cette action est irréversible. Toutes vos données seront définitivement supprimées.</p>
            <p><strong>Pour confirmer, entrez votre mot de passe :</strong></p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Mot de passe"
              className="delete-password-input"
            />
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => {
                setShowDeleteModal(false);
                setDeletePassword('');
                setError('');
              }}>
                Annuler
              </button>
              <button className="btn-delete" onClick={handleDeleteAccount}>
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
